import React from "react"
import { connect } from "react-redux"
import _ from "../../utils/lodashUtils"
import "./ChatComponent.scss"
import { Avatar, Input, Typography } from "antd"
import {
  ToastMessage,
  NOTIFICATION_TYPES,
  ToastMessageDestroy,
} from "../Notification/Notifications"
import { CloseOutlined } from "@ant-design/icons"
import { FirebaseContext, FIREBASE_STATE_KEYS } from "../../firebase"
import chatSendIcon from "../../assets/images/chat-send-icon.svg"
import moment from "moment"
import emojiIcon from "../../assets/images/emoji-icon.png"
import { Client } from "twilio-chat"
import API from "../../axiosConfig/axiosConfig"
import { ActivityIndicator } from "../ActivityIndicator"
import data from "emoji-mart/data/apple.json"
import { NimblePicker } from "emoji-mart"
import "emoji-mart/css/emoji-mart.css"
import emptyProfileIcon from "../../assets/images/profile.svg"
import {
  GET_ROBO_IMAGE,
  CHAT_WINDOW_PLACEHOLDER,
} from "../../constants/messages"
import { getPartnerNameorNickName } from "../../utils/helpers"
import { ENDPOINTS } from "../../constants/apiEndPoints"
import { emitter } from "../../utils/emitter"
import { CUSTOM_EVENTS } from "../../constants/commons"

const { TextArea } = Input
const { Text } = Typography

const ChatWindow = props => {
  /*
    Document referred for twilio: 
    https://www.twilio.com/docs/chat/access-token-lifecycle
    1. Creates a twilio channel, retrieves messages and sends messages to channel etc.
    2. Nimble picker to add emoticons.
    3. Notifications, Unread messages, typing indication.

   */
  const { visible, onClose, user, chatChannelSID, partner, dating } = props
  const [messageText, setMessageText] = React.useState()
  const [chatBottom, setChatBottom] = React.useState()
  const [showTypingIndicator, setShowTypingIndicator] = React.useState(false)
  const { partnerState, userState } = React.useContext(FirebaseContext)
  const [previousMessages, setPreviousMessages] = React.useState()
  const [channel, setChatChannel] = React.useState()
  const [loadingChats, setLoadingChats] = React.useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false)
  // Use Firebase Context for Partner Name
  const partnerName = getPartnerNameorNickName(partnerState, partner, dating)

  let userDetails = _.get(userState, "userDetails", {})
  let partnerDetails = _.get(partnerState, "userDetails", {})

  const lastSeenMessagesAt = _.get(
    userState,
    FIREBASE_STATE_KEYS.LAST_SEEN_MESSAGES_AT,
    ""
  )

  const transformMessage = message => ({
    author: message.author === user.uuid ? "me" : "them",
    type: "text",
    data: {
      text: message.body,
    },
    timestamp: _.get(message, "state.timestamp", ""),
  })

  React.useEffect(() => {
    let unreadCountSinceLastSeen = 0
    // 1. get chat token
    if (user) {
      const getTwilioChatChannelAndMessages = async () => {
        setLoadingChats(true)
        try {
          const res = await API.post(ENDPOINTS.TWILIO_TOKEN, {})
          const twilioChatToken = _.get(res, "data.auth_token")
          const client = await Client.create(twilioChatToken)
          const channel = await client.getChannelBySid(chatChannelSID)
          const messageList = (await channel.getMessages()).items
          const previousMessages = messageList.map(message => {
            const transformedMessage = transformMessage(message)
            if (
              lastSeenMessagesAt &&
              transformedMessage.timestamp > lastSeenMessagesAt
            ) {
              unreadCountSinceLastSeen++
            }
            return transformedMessage
          })
          setPreviousMessages(previousMessages)
          setChatChannel(channel)
          setLoadingChats(false)
          showUnseenMessagesNotification(unreadCountSinceLastSeen)
        } catch (err) {
          console.error("Error while getting chat token ", err)
          setLoadingChats(false)
        }
      }
      getTwilioChatChannelAndMessages()
    }
  }, [chatChannelSID])

  //open chat when cliked on message notification
  const openChat = () => {
    emitter.fire(CUSTOM_EVENTS.OPEN_CHAT_POPUP)
    // TODO: Handle re-render of chat
    const ref = _.get(props, "chatRef.current")
      ? _.get(props, "chatRef.current")
      : document.getElementById("chat-popup-container")
    window.scrollTo({
      behavior: "smooth",
      top: ref.offsetTop,
    })
    ToastMessageDestroy()
  }

  const showUnseenMessagesNotification = unreadCount => {
    if (unreadCount > 0) {
      ToastMessage({
        type: NOTIFICATION_TYPES.INFO,
        message: `You have ${unreadCount} unseen messages from ${partnerName}`,
        description: `Open chat to see them.`,
        clickedFunction: () => openChat,
        className: "class-cursor",
      })
      emitter.fire(CUSTOM_EVENTS.CHAT_SHOW_UNREAD_DOT)
    }
  }

  React.useEffect(() => {
    async function applyChatListeners() {
      if (channel) {
        //set up the listener for the typing started Channel event
        channel.on("typingStarted", function(member) {
          //process the member to show typing
          setShowTypingIndicator(true)
        })

        //set  the listener for the typing ended Channel event
        channel.on("typingEnded", function(member) {
          //process the member to stop showing typing
          setShowTypingIndicator(false)
        })

        channel.on("messageAdded", async newMessage => {
          if (newMessage.author !== user.uuid) {
            // only show notifications when messages are hidden.
            !visible &&
              ToastMessage({
                type: NOTIFICATION_TYPES.INFO,
                message: `${partnerName} messaged you`,
                description: `${newMessage.body}`,
                clickedFunction: () => openChat,
                className: "class-cursor",
              })

            !visible && emitter.fire(CUSTOM_EVENTS.CHAT_SHOW_UNREAD_DOT)
          }

          const messageList = (await channel.getMessages()).items
          const previousMessages = messageList.map(message =>
            transformMessage(message)
          )
          setPreviousMessages(previousMessages)
          scrollToChatBottom()
        })
      }
    }
    applyChatListeners()
    if (channel) {
      return () => channel.removeAllListeners()
    }
  }, [channel])

  React.useEffect(() => {
    if (visible) {
      // toggleUnreadIndicator(false) // turn off unread indicator when popup is visible.
      emitter.fire(CUSTOM_EVENTS.CHAT_HIDE_UNREAD_DOT)
    } else {
      setShowEmojiPicker(false) // turn off emoji picker when closing chat.
    }
  }, [visible])

  const chatViewRef = React.useRef()
  // why? Because we want to scroll once the ref has a 'current' value, since refs are not part of the render cycle, I've
  // used a callback ref, once set, it stores the ref in a state from where we use scrollToBottom.
  const chatViewBottomRef = React.useCallback(node => {
    if (node !== null) {
      setChatBottom(node)
    } else {
      setChatBottom(undefined)
    }
  }, [])

  // scrolls chat to the bottom.
  const scrollToChatBottom = () => {
    if (chatBottom && chatViewRef.current) {
      chatViewRef.current.scrollTo(0, chatBottom.offsetTop + 30)
    }
  }

  React.useEffect(() => {
    if (chatBottom) {
      scrollToChatBottom()
      if (channel) channel.setAllMessagesConsumed()
    }
  }, [chatBottom, previousMessages])

  React.useEffect(() => {
    if (channel) {
      return channel.removeAllListeners()
    }
  }, [])

  const onSendClick = async () => {
    if (messageText && messageText.trim()) {
      await channel.sendMessage(messageText)
      setMessageText("")
      setShowEmojiPicker(false)
    }
  }

  const keyDownHandler = event => {
    if (event.key === "Enter") {
      onSendClick()
      event.preventDefault()
    } else {
      channel && channel.typing()
    }
  }

  const toggleEmojiPicker = () =>
    setShowEmojiPicker(showEmojiPicker => !showEmojiPicker)

  const onChangeMessage = event => setMessageText(event.target.value)
  const onAddEmoji = emoji => {
    if (emoji && emoji.native) {
      setMessageText(text => `${text ? text : ""}${emoji.native}`)
    }
  }

  const getProfileThumbnail = message => {
    if (
      message.author === "me" &&
      (_.get(userDetails, "display_name") ||
        _.get(userDetails, "profile_image"))
    ) {
      return userDetails.profile_image
        ? userDetails.profile_image
        : GET_ROBO_IMAGE(userDetails.display_name)
    } else if (
      message.author === "them" &&
      (_.get(partner, "display_name") || _.get(partner, "profile_image"))
    ) {
      return partnerDetails.profile_image
        ? partnerDetails.profile_image
        : GET_ROBO_IMAGE(partnerDetails.display_name)
    } else {
      return emptyProfileIcon
    }
  }

  const checkIfLastMessageWasFromAuthor = (messages, currentIndex) => {
    if (currentIndex > 0) {
      if (messages[currentIndex - 1].author === messages[currentIndex].author) {
        return true
      }
      return false
    }
    return false
  }

  return visible ? (
    <section className={`chat-window`}>
      <React.Fragment>
        <header className="chat-header">
          <button className="close-btn" onClick={onClose}>
            <CloseOutlined />
          </button>
        </header>
        {loadingChats ? (
          <ActivityIndicator />
        ) : (
          <section className="chat-view" ref={chatViewRef}>
            <ol className="messages-list">
              {previousMessages &&
                previousMessages.map((message, index) => (
                  <div
                    className={
                      _.get(message, "author") === "me"
                        ? "chat-message-me"
                        : "chat-message"
                    }
                  >
                    <li
                      key={index}
                      className={
                        _.get(message, "author") === "me" ? "me" : "message"
                      }
                    >
                      <div className="message-details">
                        <div className="message-box">
                          <Text className="message-text">
                            <span>{_.get(message, "data.text", "-")}</span>
                          </Text>
                          <Text className="message-time">
                            <span>
                              {moment(message.timestamp).format("hh:mm A")}
                            </span>
                          </Text>
                        </div>
                      </div>
                    </li>
                    <div className="avatar">
                      {!checkIfLastMessageWasFromAuthor(
                        previousMessages,
                        index
                      ) && (
                        <Avatar size={25} src={getProfileThumbnail(message)} />
                      )}
                    </div>
                  </div>
                ))}
              {showTypingIndicator && (
                <li className="indicator">
                  <TypingIndicator />
                </li>
              )}
              <li className="chat-view-bottom" ref={chatViewBottomRef}></li>
            </ol>
          </section>
        )}
        {showEmojiPicker && (
          <NimblePicker
            set="apple"
            data={data}
            sheetSize={32}
            showPreview={false}
            theme={"auto"}
            onSelect={onAddEmoji}
            style={{
              width: "100%",
              position: "absolute",
              height: "82%",
              overflow: "auto",
              left: 0,
              top: 0,
              background: "#191919",
            }}
          />
        )}
      </React.Fragment>
      <section className="chat-input-container">
        <TextArea
          placeholder={CHAT_WINDOW_PLACEHOLDER}
          autoSize={{ minRows: 1, maxRows: 4 }}
          onChange={onChangeMessage}
          value={messageText}
          onKeyDown={keyDownHandler}
        />
        <button className="emoji-btn" onClick={toggleEmojiPicker}>
          <img src={emojiIcon} alt="emoji" />
        </button>
        <button
          className="send-btn"
          onClick={onSendClick}
          disabled={_.isEmpty(messageText)}
        >
          <img src={chatSendIcon} alt="send" />
        </button>
      </section>
    </section>
  ) : null
}

const TypingIndicator = () => {
  return (
    <div className="typing-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    channelName: _.get(
      state,
      "invitePeople.partnerDetails.partner1.username",
      ""
    ),
    dating: state.invitePeople.partnerDetails,
    user: state.auth.user,
    partner: state.auth.partner,
    partnerDetails: _.get(state, "invitePeople.partnerDetails", {}),
    chatChannelSID: _.get(
      state,
      "invitePeople.partnerDetails.chat_channel_sid",
      ""
    ),
  }
}

export default connect(mapStateToProps, {})(ChatWindow)
